/**
 * Authentication context and hooks
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  authApi,
  userApi,
  setTokens,
  clearTokens,
  getAccessToken,
  type User,
} from './api';
import {
  generateKeyPair,
  encryptPrivateKey,
  decryptPrivateKey,
  storeSecretKey,
  getSecretKey,
  clearSecretKey,
} from './encryption';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check for existing session on mount
  useEffect(() => {
    async function checkAuth() {
      const token = getAccessToken();
      const secretKey = getSecretKey();

      if (token && secretKey) {
        try {
          const profile = await userApi.getProfile();
          setUser(profile);
        } catch {
          // Token invalid, clear everything
          clearTokens();
          clearSecretKey();
        }
      }

      setLoading(false);
    }

    checkAuth();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setError(null);
    setLoading(true);

    try {
      const response = await authApi.login(email, password);

      // Decrypt the private key
      const secretKey = await decryptPrivateKey(
        response.user.encryptedPrivateKey,
        password
      );

      if (!secretKey) {
        throw new Error('Failed to decrypt encryption key. Please check your password.');
      }

      // Store tokens and secret key
      setTokens(response.accessToken, response.refreshToken);
      storeSecretKey(secretKey);

      // Fetch full profile
      const profile = await userApi.getProfile();
      setUser(profile);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (email: string, password: string) => {
    setError(null);
    setLoading(true);

    try {
      // Generate keypair for E2E encryption
      const { publicKey, secretKey } = generateKeyPair();

      // Encrypt private key with password
      const encryptedPrivateKey = await encryptPrivateKey(secretKey, password);

      // Register with server
      const response = await authApi.register({
        email,
        password,
        encryptedPrivateKey,
        publicKey,
      });

      // Store tokens and secret key
      setTokens(response.accessToken, response.refreshToken);
      storeSecretKey(secretKey);

      // Fetch full profile
      const profile = await userApi.getProfile();
      setUser(profile);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem('silentjobhunter_refresh_token');
      if (refreshToken) {
        await authApi.logout(refreshToken);
      }
    } catch {
      // Ignore errors during logout
    } finally {
      clearTokens();
      clearSecretKey();
      setUser(null);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        login,
        register,
        logout,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
